class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.liveSearchEnabled = this.hasAttribute('data-live-search');
    this.sectionId = this.dataset.sectionId;
    this.searchUrl = this.input.form.action;
    this.searchAbortController = null;
    this.setupEventListeners();
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
      this.updateLiveSearch('');
    }
  }

  onChange(event) {
    super.onChange(event);
    this.updateLiveSearch(event.target.value.trim());
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      const searchHeader = this.closest('.template-search__header');
      searchHeader?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  onFormSubmit(event) {
    const searchTerm = this.input.value.trim();
    if (!this.liveSearchEnabled || !searchTerm) return;

    event.preventDefault();
    const pageUrl = new URL(this.searchUrl);
    pageUrl.searchParams.set('q', searchTerm);
    pageUrl.searchParams.set('type', 'product');
    pageUrl.searchParams.set('options[prefix]', 'last');
    window.location.assign(pageUrl);
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }

  updateLiveSearch(searchTerm) {
    if (!this.liveSearchEnabled || !this.sectionId) return;

    this.searchAbortController?.abort();
    const abortController = new AbortController();
    this.searchAbortController = abortController;
    this.setAttribute('loading', '');

    const params = new URLSearchParams({
      section_id: this.sectionId,
    });

    if (searchTerm) {
      params.set('q', searchTerm);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
    }

    fetch(`${this.searchUrl}?${params.toString()}`, {
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        return response.text();
      })
      .then((markup) => {
        const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
        const nextResults = parsedDocument.querySelector(`#LiveSearchResults-${this.sectionId}`);
        const nextMeta = parsedDocument.querySelector(`#LiveSearchMeta-${this.sectionId}`);
        const currentResults = document.querySelector(`#LiveSearchResults-${this.sectionId}`);
        const currentMeta = document.querySelector(`#LiveSearchMeta-${this.sectionId}`);

        if (!nextResults || !currentResults) throw new Error('Search results container was not found');

        currentResults.innerHTML = nextResults.innerHTML;
        if (nextMeta && currentMeta) currentMeta.innerHTML = nextMeta.innerHTML;
        rankSearchResults(currentResults, searchTerm);

        const pageUrl = new URL(this.searchUrl);
        if (searchTerm) {
          pageUrl.searchParams.set('q', searchTerm);
          pageUrl.searchParams.set('type', 'product');
          pageUrl.searchParams.set('options[prefix]', 'last');
        }
        window.history.replaceState({}, '', pageUrl);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      })
      .finally(() => {
        if (this.searchAbortController === abortController) this.removeAttribute('loading');
      });
  }

}

customElements.define('main-search', MainSearch);

function normalizeSearchValue(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

function getSearchTitleScore(title, searchTerm) {
  const normalizedTitle = normalizeSearchValue(title);
  const normalizedQuery = normalizeSearchValue(searchTerm);
  const queryTerms = normalizedQuery.split(/\s+/).filter(Boolean);
  const titleTerms = normalizedTitle.split(/\s+/).filter(Boolean);

  if (!normalizedTitle || !queryTerms.length) return 0;
  if (normalizedTitle.includes(normalizedQuery)) return 400;

  const prefixMatches = queryTerms.filter((queryTerm) =>
    titleTerms.some((titleTerm) => titleTerm.startsWith(queryTerm))
  ).length;
  const containsMatches = queryTerms.filter((queryTerm) => normalizedTitle.includes(queryTerm)).length;

  if (prefixMatches === queryTerms.length) return 300;
  if (containsMatches === queryTerms.length) return 200;
  return Math.max(prefixMatches, containsMatches) * 10;
}

function rankSearchResults(root = document, searchTerm) {
  const grid = root.querySelector?.('.product-grid') || document.querySelector('.template-search__results .product-grid');
  if (!grid) return;

  const items = [...grid.querySelectorAll(':scope > [data-search-product-item]')];
  if (items.length < 2) return;

  items
    .map((item, index) => ({
      item,
      index,
      score: getSearchTitleScore(item.dataset.searchProductTitle, searchTerm),
    }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .forEach(({ item }) => grid.appendChild(item));
}

document.addEventListener('DOMContentLoaded', () => {
  const searchInput = document.querySelector('main-search[data-live-search] input[type="search"]');
  if (searchInput) rankSearchResults(document, searchInput.value);
});

document.addEventListener('click', (event) => {
  const arrow = event.target.closest('[data-search-alternatives-previous], [data-search-alternatives-next]');
  if (arrow) {
    const carousel = arrow.closest('[data-search-alternatives-carousel]');
    const track = carousel?.querySelector('[data-search-alternatives-track]');
    if (!track) return;

    const cards = [...track.querySelectorAll('.search-alternatives-carousel__card')];
    if (cards.length < 2) return;

    const currentPosition = track.scrollLeft;
    const isPrevious = arrow.hasAttribute('data-search-alternatives-previous');
    const tolerance = 4;
    const trackLeft = track.getBoundingClientRect().left;
    const cardPositions = cards.map((card) => ({
      card,
      left: card.getBoundingClientRect().left - trackLeft + currentPosition,
    }));
    let target;

    if (isPrevious) {
      target =
        [...cardPositions].reverse().find((position) => position.left < currentPosition - tolerance) ||
        cardPositions[0];
    } else {
      target =
        cardPositions.find((position) => position.left > currentPosition + tolerance) ||
        cardPositions[cardPositions.length - 1];
    }

    track.scrollTo({
      left: target.left,
      behavior: 'smooth',
    });
    window.setTimeout(() => updateSearchAlternativeArrows(carousel), 350);
  }
});

function updateSearchAlternativeArrows(carousel) {
  const track = carousel?.querySelector('[data-search-alternatives-track]');
  const previous = carousel?.querySelector('[data-search-alternatives-previous]');
  const next = carousel?.querySelector('[data-search-alternatives-next]');
  if (!track || !previous || !next) return;

  const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
  previous.disabled = track.scrollLeft <= 2;
  next.disabled = track.scrollLeft >= maxScroll - 2;
}

function initializeSearchAlternativeCarousels(root = document) {
  root.querySelectorAll('[data-search-alternatives-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-search-alternatives-track]');
    if (!track || track.dataset.arrowsInitialized === 'true') return;

    track.dataset.arrowsInitialized = 'true';
    track.addEventListener('scroll', () => updateSearchAlternativeArrows(carousel), { passive: true });
    window.requestAnimationFrame(() => updateSearchAlternativeArrows(carousel));
    window.setTimeout(() => updateSearchAlternativeArrows(carousel), 250);
  });
}

document.addEventListener('DOMContentLoaded', () => initializeSearchAlternativeCarousels());

const searchAlternativesObserver = new MutationObserver((mutations) => {
  if (mutations.some((mutation) => mutation.addedNodes.length > 0)) {
    initializeSearchAlternativeCarousels();
  }
});

searchAlternativesObserver.observe(document.documentElement, { childList: true, subtree: true });
window.addEventListener('resize', () => {
  document
    .querySelectorAll('[data-search-alternatives-carousel]')
    .forEach((carousel) => updateSearchAlternativeArrows(carousel));
});
